import HandymanIcon from '@mui/icons-material/Handyman';
import WorkIcon from '@mui/icons-material/Work';
import { Divider, Stack, Typography } from '@mui/material';
import { ContactAbout, ContactDetails } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contactDetails: ContactDetails | null;
  aboutContact: ContactAbout | null;

  qualificationToText(): string;
}

const AboutContact: React.FC<Props> = ({ contactDetails, aboutContact, qualificationToText }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <Typography className={classes.boldText}>
        {t('whyIsContactQualified', { fullName: contactDetails?.fullName, qualification: qualificationToText() })}
      </Typography>
      <div className={classes.infoContainer}>
        <div className={classes.infoContainerHeader}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <WorkIcon className={classes.icon} />
            <Typography className={classes.boldText}>{t('professionSummary', { fullName: contactDetails?.fullName })}</Typography>
          </Stack>
        </div>
        <Stack className={classes.infoContainerContent} gap={1}>
          {aboutContact?.summary ? (
            <>
              <div className={classes.greyText}>{t('summary')}</div>
              <Typography className={classes.text}>{aboutContact.summary}</Typography>
              <Divider />
            </>
          ) : null}
          <div className={classes.greyText}>{t('experience')}</div>
          {aboutContact?.experience?.map((item, index) => {
            const startDate = typeof item.startDate === 'string' ? item.startDate : `${item.startDate?.month || ''} / ${item.startDate?.year || ''}`;
            const endDate = !_.isNil(item.endDate)
              ? typeof item.endDate === 'string'
                ? t(_.camelCase(item.endDate))
                : `${item.endDate?.month} / ${item.endDate?.year}`
              : t('present');
            const duration = startDate && endDate ? `${startDate} - ${endDate}` : startDate ? startDate : endDate ? endDate : '';

            return <PaiListItem key={index} text={item.title} subText={`${item.companyName} (${duration})`} size={'large'} disablePadding={true} />;
          })}
          <Divider />
          <div className={classes.greyText}>{t('education')}</div>
          {aboutContact?.education?.map((item, index) => {
            const startDate = typeof item.startDate === 'string' ? item.startDate : `${item.startDate?.year || ''}`;
            const endDate = !_.isNil(item.endDate)
              ? typeof item.endDate === 'string'
                ? t(_.camelCase(item.endDate))
                : `${item.endDate?.year}`
              : t('present');
            const duration = startDate && endDate ? `${startDate} - ${endDate}` : startDate ? startDate : endDate ? endDate : '';

            return (
              <PaiListItem
                key={index}
                text={`${item.degreeName || t('na')} ${!_.isNil(item.fieldOfStudy) ? `(${item.fieldOfStudy})` : ''} `}
                subText={`${item.schoolName || t('na')} (${duration})`}
                size={'large'}
                disablePadding={true}
              />
            );
          })}
        </Stack>
      </div>
      <div className={classes.infoContainer}>
        <div className={classes.infoContainerHeader}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <HandymanIcon className={classes.icon} />
            <Typography className={classes.boldText}>{t('keySkills', { fullName: contactDetails?.fullName })}</Typography>
          </Stack>
        </div>
        <Stack className={classes.infoContainerContent} gap={1}>
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <Typography className={classes.greyText}>{t('languages')}</Typography>
            <Typography className={classes.greyText}>|</Typography>
            {aboutContact?.languages?.length ? (
              aboutContact.languages.map((item, index) => (
                <div className={classes.skillItem} key={index}>
                  {item}
                </div>
              ))
            ) : (
              <Typography className={classes.greyText}>-</Typography>
            )}
          </Stack>
          <Divider />
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <Typography className={classes.greyText}>{t('technicalSkills')}</Typography>
            <Typography className={classes.greyText}>|</Typography>
            {aboutContact?.skills.technical.length ? (
              aboutContact.skills.technical.map((item, index) => (
                <div className={classes.skillItem} key={index}>
                  {item}
                </div>
              ))
            ) : (
              <Typography className={classes.greyText}>-</Typography>
            )}
          </Stack>
          <Divider />
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <Typography className={classes.greyText}>{t('industrySkills')}</Typography>
            <Typography className={classes.greyText}>|</Typography>
            {aboutContact?.skills.industry.length ? (
              aboutContact.skills.industry.map((item, index) => (
                <div className={classes.skillItem} key={index}>
                  {item}
                </div>
              ))
            ) : (
              <Typography className={classes.greyText}>-</Typography>
            )}
          </Stack>
          <Divider />
          <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
            <Typography className={classes.greyText}>{t('softSkills')}</Typography>
            <Typography className={classes.greyText}>|</Typography>
            {aboutContact?.skills.softSkills.length ? (
              aboutContact.skills.softSkills.map((item, index) => (
                <div className={classes.skillItem} key={index}>
                  {item}
                </div>
              ))
            ) : (
              <Typography className={classes.greyText}>-</Typography>
            )}
          </Stack>
        </Stack>
      </div>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  boldText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'pre-wrap',
  },
  infoContainer: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '8px',
  },
  infoContainerHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  infoContainerContent: {
    padding: '8px 16px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.primary.main,
  },
  skillItem: {
    fontSize: theme.typography.pxToRem(13),
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '4px',
    padding: '0 4px',
  },
}));

export default AboutContact;
